/**
 * A ponte entre o simulador portado e as peças de interface da casca.
 *
 * O PROBLEMA QUE ELA RESOLVE
 * O simulador tinha o próprio toast (`#toast`) e o próprio diálogo (`#ov-cfm`). A casca
 * tem os dela — os mesmos EL-166 e EL-169, portados no lote anterior. Ao descartar a
 * marcação duplicada, os do simulador ficaram sem alvo, e o resultado NÃO foi um erro:
 *   · `flash()` começa com `if(!el) return`, então as **78 chamadas** viraram silêncio.
 *     O simulador perdeu a voz: nem "cenário salvo", nem aviso de erro, nem "Desfazer".
 *   · `confirmar()` cai para `window.confirm()` quando não acha o diálogo, então as **10
 *     confirmações** voltaram para a caixa cinza do navegador — exatamente a que o EL-169
 *     foi criado para eliminar.
 * Nenhum dos dois aparece no console. Foram achados pela varredura.
 *
 * A correção não é pôr guarda: é RELIGAR. Um toast e um diálogo no aplicativo inteiro.
 */
import { avisar, confirmar } from '../ui/aviso';
import type { Tom } from '../ui/aviso';

/** O que o `flash` da v67 aceita como 2º argumento. */
interface OpcoesFlash {
  tipo?: string;
  undo?: (() => void) | null;
}

/**
 * `flash(msg, opt)` da v67. `opt` pode ser um objeto **ou uma função** — nesse caso é o
 * desfazer, e essa forma abreviada é usada de verdade no arquivo (linha 182). Perder isso
 * transformaria a função de desfazer num objeto de opções ignorado, sem erro.
 */
export function flash(msg: string, opt?: OpcoesFlash | (() => void)): void {
  const o: OpcoesFlash = typeof opt === 'function' ? { undo: opt } : (opt ?? {});
  const tom: Tom = o.tipo === 'ok' || o.tipo === 'erro' || o.tipo === 'aviso' ? o.tipo : 'info';
  avisar(msg, { tom, desfazer: typeof o.undo === 'function' ? o.undo : undefined });
}

/** O que o `confirmar` da v67 aceita. */
interface OpcoesConfirmar {
  titulo?: string;
  corpo?: string;
  rotuloOk?: string;
  destrutivo?: boolean;
  /** abre com o foco em "Cancelar" — a pergunta que nasce de um gesto de digitação (revisão do lote 2) */
  focarCancelar?: boolean;
}

/**
 * `confirmar(o, onOk)` da v67 — estilo retorno de chamada. O da casca devolve promessa,
 * então a ponte traduz. `onOk` só roda na confirmação; Esc, ✕ e clique fora resolvem
 * como cancelamento, igual à v67.
 *
 * ⚠ O `corpo` do simulador vem com marcação (negrito no nome do que vai ser excluído) e
 * é montado no código, nunca com texto vindo do banco — por isso segue como HTML. Se um
 * dia alguém interpolar nome de empreendimento aí, tem de passar por escape antes.
 */
export function confirmarLegado(o: OpcoesConfirmar, onOk?: () => void): void {
  void confirmar({
    titulo: o.titulo ?? 'Confirmar',
    corpo: o.corpo ?? '',
    confirmar: o.rotuloOk ?? 'Confirmar',
    perigo: o.destrutivo === true,
    corpoEhHtml: true,
    focarCancelar: o.focarCancelar === true,
  }).then((ok) => {
    if (ok && typeof onOk === 'function') onOk();
  });
}
